import { createFileRoute } from '@tanstack/react-router'
import { z } from 'zod'

const paymentsSearchSchema = z.object({
    page: z.number().int().positive().catch(1),
    // Accept both string and number for search, then convert to string
    search: z.union([z.string(), z.number()]).transform(val => String(val)).optional(),
    status: z.string().optional(),
    direction: z.string().optional(),
})

import { useState, useEffect } from 'react';
import { useNavigate, useRouterState } from '@tanstack/react-router';
import Layout from '../../../components/Layout';
import { useI18n } from '../../../hooks/useI18n';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';

import { formatAmountSummary, formatDate } from '../../../utils/formatters';
import { apiFetch } from '../../../utils/api';
import PaginationFooter from '../../../components/PaginationFooter';
import { Search, ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';
import PaymentAdvancedFilter from '../../../components/PaymentAdvancedFilter';
import { CurrentUserProvider } from '../../../hooks/useCurrentUser';
import { TagFilter } from '@/components/TagFilter';


interface Vendor {
    id: string;
    name: string;
}

interface CostCenter {
    id: string;
    name: string;
    is_active: boolean;
}

interface PaymentInstrument {
    id: string;
    name: string;
    instrument_type: string;
    company?: {
        id: string;
        name: string;
    };
}

interface Payment {
    id: string;
    vendor?: Vendor;
    cost_center?: CostCenter;
    payment_instrument?: PaymentInstrument;
    direction: string;
    amount: number;
    currency: string;
    amount_accounting?: number;
    value_date?: string;
    received_at?: string;
    status: string;
    invoice_reference?: string;
    external_id?: string;
    created_at: string;
}

interface Filters {
    status: string;
    direction: string;
    search: string;
}

interface AdvancedFilters {
    cost_center_id?: string;
    vendor_id?: string;
    payment_instrument_id?: string;
    date_from?: string;
    date_to?: string;
    currency?: string;
    min_amount?: string;
    max_amount?: string;
}

interface Pagination {
    page: number;
    total: number;
    pages: number;
    limit: number;
}

const statusOptions = (t: (key: string, namespace?: string, options?: Record<string, any>) => string) => ([
    { value: 'all', label: t('payments.filters.status.all', 'finance') },
    { value: 'pending', label: t('payments.filters.status.pending', 'finance') },
    { value: 'posted', label: t('payments.filters.status.posted', 'finance') },
    { value: 'voided', label: t('payments.filters.status.voided', 'finance') },
    { value: 'reversed', label: t('payments.filters.status.reversed', 'finance') },
]);

const directionOptions = (t: (key: string, namespace?: string, options?: Record<string, any>) => string) => ([
    { value: 'all', label: t('payments.filters.direction.all', 'finance') },
    { value: 'AR', label: t('payments.filters.direction.ar', 'finance') },
    { value: 'AP', label: t('payments.filters.direction.ap', 'finance') },
]);



const getStatusVariant = (status: string): "default" | "secondary" | "destructive" | "outline" => {
    switch (status) {
        case 'posted':
            return 'default';
        case 'pending':
            return 'secondary';
        case 'voided':
        case 'reversed':
            return 'destructive';
        default:
            return 'outline';
    }
};


export default function PaymentsPage() {
    const navigate = useNavigate();
    const routerState = useRouterState();
    const { t } = useI18n();
    const [payments, setPayments] = useState<Payment[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [filters, setFilters] = useState<Filters>({
        status: '',
        direction: '',
        search: '',
    });
    const [advancedFilters, setAdvancedFilters] = useState<AdvancedFilters>({});
    const [pagination, setPagination] = useState<Pagination>({
        page: 1,
        total: 0,
        pages: 0,
        limit: 20,
    });

    // Debounced search to avoid too many API calls
    const [debouncedSearch, setDebouncedSearch] = useState<string>('');
    const [isInitialized, setIsInitialized] = useState<boolean>(false);
    const [tagFilteredIds, setTagFilteredIds] = useState<string[] | null>(null);

    // Sorting state
    const [sortBy, setSortBy] = useState<string>('value_date');
    const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

    // Initialize state from URL parameters on component mount
    useEffect(() => {
        if (!isInitialized) {
            const searchParams = routerState.location.search;
            const { page, limit, status, direction, search } = searchParams;

            // Update filters from URL
            setFilters({
                status: (status as string) || '',
                direction: (direction as string) || '',
                search: (search as string) || '',
            });

            // Update pagination from URL
            setPagination(prev => ({
                ...prev,
                page: parseInt(String(page || '1'), 10),
                limit: parseInt(String(limit || '20'), 10),
            }));

            // Set debounced search immediately from URL to avoid delay
            setDebouncedSearch((search as string) || '');

            setIsInitialized(true);
        }
    }, [isInitialized]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(filters.search);
        }, 300);

        return () => clearTimeout(timer);
    }, [filters.search]);

    useEffect(() => {
        if (isInitialized) {
            fetchPayments();
        }
    }, [isInitialized, filters.status, filters.direction, debouncedSearch, pagination.page, pagination.limit, advancedFilters, sortBy, sortDir]);



    const fetchPayments = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({
                page: pagination.page.toString(),
                limit: pagination.limit.toString(),
            });

            if (filters.status && filters.status !== 'all') {
                params.append('status', filters.status);
            }
            if (filters.direction && filters.direction !== 'all') {
                params.append('direction', filters.direction);
            }
            if (debouncedSearch) {
                params.append('search', debouncedSearch);
            }

            // Add sorting parameters
            if (sortBy) {
                params.append('sort_by', sortBy);
            }
            if (sortDir) {
                params.append('sort_dir', sortDir);
            }

            // Add advanced filters
            Object.entries(advancedFilters).forEach(([key, value]) => {
                if (value && value !== '') {
                    params.append(key, value);
                }
            });

            const response = await apiFetch(`/api/payments/?${params.toString()}`);
            if (response.ok) {
                const data = await response.json();
                setPayments(data.payments || []);

                setPagination(prev => ({
                    ...prev,
                    total: data.total || 0,
                    pages: data.pagination?.pages || 0,
                }));
            } else {
                console.error('API response not ok:', response.status, response.statusText);
                setPayments([]);
            }
        } catch (error) {
            console.error('Error fetching payments:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (key: keyof Filters, value: string) => {
        setFilters(prev => ({ ...prev, [key]: value }));
        setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page
    };

    const handlePageChange = (newPage: number) => {
        setPagination(prev => ({ ...prev, page: newPage }));
    };

    const clearFilters = () => {
        setFilters({
            status: '',
            direction: '',
            search: '',
        });
        setAdvancedFilters({});
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    const handleAdvancedFiltersChange = (newFilters: AdvancedFilters) => {
        setAdvancedFilters(newFilters);
        setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page
    };

    const clearAdvancedFilters = () => {
        setAdvancedFilters({});
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    const handleSort = (column: string) => {
        const newDir: 'asc' | 'desc' = sortBy === column ? (sortDir === 'asc' ? 'desc' : 'asc') : 'asc';
        setSortBy(column);
        setSortDir(newDir);
        setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page
    };

    const hasActiveFilters = filters.status || filters.direction || filters.search ||
        Object.values(advancedFilters).some(value =>
            value !== undefined && value !== '' &&
            (Array.isArray(value) ? value.length > 0 : true)
        );

    return (
        <CurrentUserProvider>
            <Layout>
                <div className="space-y-6">
                    <div className="flex justify-between items-center">
                        <h1 className="text-3xl font-bold tracking-tight">{t('navigation.payments', 'common')}</h1>
                        <Button onClick={() => navigate({ to: '/payments/new' })}>
                            {t('payments.add', 'finance')}
                        </Button>
                    </div>

                    {/* Filters */}
                    <div className="flex flex-wrap gap-4 mb-4">
                        <div className="relative flex-1 min-w-[200px]">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                            <Input
                                placeholder={t('payments.filters.searchPlaceholder', 'finance')}
                                value={filters.search}
                                onChange={(e) => handleFilterChange('search', e.target.value)}
                                className="pl-10"
                            />
                        </div>

                        <Select value={filters.status} onValueChange={(value) => handleFilterChange('status', value)}>
                            <SelectTrigger className="w-48">
                                <SelectValue placeholder={t('payments.filters.statusPlaceholder', 'finance')} />
                            </SelectTrigger>
                            <SelectContent>
                                {statusOptions(t).map((option) => (
                                    <SelectItem key={option.value} value={option.value}>
                                        {option.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>

                        <Select value={filters.direction} onValueChange={(value) => handleFilterChange('direction', value)}>
                            <SelectTrigger className="w-48">
                                <SelectValue placeholder={t('payments.filters.directionPlaceholder', 'finance')} />
                            </SelectTrigger>
                            <SelectContent>
                                {directionOptions(t).map((option) => (
                                    <SelectItem key={option.value} value={option.value}>
                                        {option.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>

                        <TagFilter
                            entityType="payments"
                            onFilterChange={(selection) => {
                                setTagFilteredIds(selection ? selection.tagIds : null);
                            }}
                        />

                        <Button variant="outline" onClick={clearFilters}>
                            {t('actions.clearFilters', 'common')}
                        </Button>
                    </div>

                    {/* Advanced Filters */}
                    <PaymentAdvancedFilter
                        filters={advancedFilters}
                        onFiltersChange={handleAdvancedFiltersChange}
                        onClear={clearAdvancedFilters}
                    />

                    {/* Clear Filters */}
                    {hasActiveFilters && (
                        <div className="flex justify-between items-center">
                            <span className="text-sm text-muted-foreground">
                                {t('payments.filters.active', 'finance')}
                            </span>
                            <Button variant="outline" onClick={clearFilters}>
                                {t('actions.clearAllFilters', 'common')}
                            </Button>
                        </div>
                    )}

                    {/* Payments Table */}
                    <div className="">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead onClick={() => handleSort('value_date')} className="cursor-pointer select-none">
                                        <span className="inline-flex items-center gap-1">
                                            {t('payments.valueDate', 'finance')}
                                            {sortBy === 'value_date' ? (sortDir === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 text-muted-foreground" />}
                                        </span>
                                    </TableHead>
                                    <TableHead onClick={() => handleSort('payment_instrument_name')} className="cursor-pointer select-none">
                                        <span className="inline-flex items-center gap-1">
                                            {t('payments.paymentInstrument', 'finance')}
                                            {sortBy === 'payment_instrument_name' ? (sortDir === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 text-muted-foreground" />}
                                        </span>
                                    </TableHead>
                                    <TableHead onClick={() => handleSort('vendor_name')} className="cursor-pointer select-none">
                                        <span className="inline-flex items-center gap-1">
                                            {t('payments.vendor', 'finance')}
                                            {sortBy === 'vendor_name' ? (sortDir === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 text-muted-foreground" />}
                                        </span>
                                    </TableHead>
                                    <TableHead onClick={() => handleSort('company_name')} className="cursor-pointer select-none">
                                        <span className="inline-flex items-center gap-1">
                                            {t('payments.company', 'finance')}
                                            {sortBy === 'company_name' ? (sortDir === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 text-muted-foreground" />}
                                        </span>
                                    </TableHead>

                                    <TableHead onClick={() => handleSort('invoice_reference')} className="cursor-pointer select-none">
                                        <span className="inline-flex items-center gap-1">
                                            {t('payments.invoiceReference', 'finance')}
                                            {sortBy === 'invoice_reference' ? (sortDir === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 text-muted-foreground" />}
                                        </span>
                                    </TableHead>
                                    <TableHead onClick={() => handleSort('amount')} className="cursor-pointer select-none text-right">
                                        <span className="inline-flex items-center gap-1">
                                            {t('payments.amount', 'finance')}
                                            {sortBy === 'amount' ? (sortDir === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 text-muted-foreground" />}
                                        </span>
                                    </TableHead>
                                    <TableHead onClick={() => handleSort('status')} className="cursor-pointer select-none">
                                        <span className="inline-flex items-center gap-1">
                                            {t('payments.status', 'finance')}
                                            {sortBy === 'status' ? (sortDir === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 text-muted-foreground" />}
                                        </span>
                                    </TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {loading ? (
                                    Array.from({ length: 5 }).map((_, index) => (
                                        <TableRow key={index}>
                                            <TableCell><Skeleton className="h-4 w-28" /></TableCell>
                                            <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                                            <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                                            <TableCell><Skeleton className="h-4 w-32" /></TableCell>
                                            <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                                            <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                                            <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                                        </TableRow>
                                    ))
                                ) : payments.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                                            No payments found
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    payments
                                        .filter((payment) => !tagFilteredIds || tagFilteredIds.includes(payment.id))
                                        .map((payment) => {
                                            // Calculate signed amount based on direction
                                            const signedAmount = payment.direction === 'AP' ? -payment.amount : payment.amount;
                                            return (
                                                <TableRow
                                                    key={payment.id}
                                                    className="cursor-pointer hover:bg-muted/50"
                                                    onClick={() => navigate({ to: `/payments/${payment.id}` })}
                                                >
                                                    <TableCell>
                                                        {payment.value_date ? formatDate(payment.value_date) : '-'}
                                                    </TableCell>
                                                    <TableCell>
                                                        {payment.payment_instrument?.name || '-'}
                                                    </TableCell>
                                                    <TableCell>
                                                        {payment.vendor?.name || '-'}
                                                    </TableCell>
                                                    <TableCell>
                                                        {payment.payment_instrument?.company?.name || '-'}
                                                    </TableCell>
                                                    <TableCell>
                                                        {payment.invoice_reference || '-'}
                                                    </TableCell>
                                                    <TableCell className="text-right">
                                                        <span className={signedAmount > 0 ? 'text-green-600 font-medium' : 'text-red-600 font-medium'}>
                                                            {formatAmountSummary(signedAmount, payment.currency)}
                                                        </span>
                                                    </TableCell>
                                                    <TableCell>
                                                        <div className="flex items-center space-x-2">
                                                            <Badge variant={getStatusVariant(payment.status)}>
                                                                {payment.status}
                                                            </Badge>
                                                        </div>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })
                                )}
                            </TableBody>
                        </Table>
                    </div>

                    {/* Pagination */}
                    <PaginationFooter
                        pagination={pagination}
                        onPageChange={handlePageChange}
                        onPageSizeChange={(pageSize) => {
                            setPagination(prev => ({ ...prev, limit: pageSize, page: 1 }));
                        }}
                    />
                </div>
            </Layout>
        </CurrentUserProvider>
    );
}


export const Route = createFileRoute('/payments/')({
    validateSearch: paymentsSearchSchema,
    component: PaymentsPage,
})
